/**
 * Smooth scroll utility functions
 */

/**
 * Smooth scroll to a specific element by ID
 * @param {string} elementId - The ID of the element to scroll to
 * @param {number} offset - Optional offset from the element (default: 0)
 * @param {string} behavior - Scroll behavior (default: 'smooth')
 */
export const scrollToElement = (elementId, offset = 0, behavior = 'smooth') => {
    try {
        const element = document.getElementById(elementId);
        
        if (element) {
            const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
            const offsetPosition = elementPosition - offset;
            
            window.scrollTo({
                top: offsetPosition,
                behavior: behavior
            });
            
            return true;
        } else {
            // console.warn(`Element with ID "${elementId}" not found`);
            return false;
        }
    } catch (error) {
        console.error('Error scrolling to element:', error);
        return false;
    }
};

/**
 * Smooth scroll to the top of the page
 * @param {string} behavior - Scroll behavior (default: 'smooth')
 */
export const scrollToTop = (behavior = 'smooth') => {
    try {
        window.scrollTo({
            top: 0,
            behavior: behavior
        });
        return true;
    } catch (error) {
        console.error('Error scrolling to top:', error);
        return false;
    }
};

/**
 * Smooth scroll to booking form or top of page as fallback
 * @param {number} headerOffset - Offset for fixed headers (default: 80)
 */
export const scrollToBookingForm = (headerOffset = 80) => {
    // First try to scroll to booking form
    const bookingFormFound = scrollToElement('booking-form', headerOffset);
    
    // If booking form not found, scroll to top
    if (!bookingFormFound) {
        // console.info('Booking form not found, scrolling to top instead');
        return scrollToTop();
    }
    
    return bookingFormFound;
};

/**
 * Check if an element is in viewport
 * @param {string} elementId - The ID of the element to check
 * @returns {boolean} - True if element is in viewport
 */
export const isElementInViewport = (elementId) => {
    try {
        const element = document.getElementById(elementId);
        if (!element) return false;
        
        const rect = element.getBoundingClientRect();
        return (
            rect.top >= 0 &&
            rect.left >= 0 &&
            rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
            rect.right <= (window.innerWidth || document.documentElement.clientWidth)
        );
    } catch (error) {
        console.error('Error checking viewport:', error);
        return false;
    }
};

/**
 * Scroll with custom easing function
 * @param {number} targetPosition - Target scroll position
 * @param {number} duration - Duration in milliseconds (default: 800)
 */
export const scrollWithEasing = (targetPosition, duration = 800) => {
    const startPosition = window.pageYOffset;
    const distance = targetPosition - startPosition;
    let startTime = null;

    // Easing function (ease-in-out-cubic)
    const easeInOutCubic = (t) => {
        return t < 0.5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1;
    };

    const animateScroll = (currentTime) => {
        if (startTime === null) startTime = currentTime;
        const timeElapsed = currentTime - startTime;
        const progress = Math.min(timeElapsed / duration, 1);
        const easedProgress = easeInOutCubic(progress);
        
        window.scrollTo(0, startPosition + (distance * easedProgress));
        
        if (timeElapsed < duration) {
            requestAnimationFrame(animateScroll);
        }
    };

    requestAnimationFrame(animateScroll);
};
